import { afterApprovalStatuses, afterRejectionStatuses } from '@/lib/constants/med-requests'
import { isMpsMedRequest } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import type { MedRequestMutationFnProps } from './action'

/**
 * Every medication of an MPS family is a med request of its own and the whole family sits in
 * clinical review together (the queue shows a single entry for it — see dedupeMpsMedRequests),
 * so a decision taken on the opened request has to be sent to each sibling too or they stay
 * pending. These are the family rows still awaiting that decision.
 */
export const pendingMpsFamilyRows = (medRequest: MedRequest): MedRequest[] => {
  const settledStages = [...afterApprovalStatuses, ...afterRejectionStatuses].map(stage => stage.toLowerCase())

  // Only the medication orders take a med-req /update — the 'mps' rows are the subscription's
  // own mirror items (the swap/remove targets, via mpsHash), not actionable med requests.
  return (medRequest.mpsMedRequests ?? []).filter(
    row => row.evaroType?.toLowerCase() !== 'mps' && !settledStages.includes(row.stage?.toLowerCase() ?? '')
  )
}

/**
 * The reject / follow-up (hold) actions for the treatments of an MPS family beyond the opened
 * request: one per sibling med request still pending, each carrying only the id and the reason.
 * Unlike approve, nothing is synced to the subscription first — a decision only reaches the
 * treatments that already exist as med requests, so a treatment card added locally (no med
 * request id yet) is left alone; the changes on it stay in the store until an approve.
 *
 * Everything patient-facing stays on the opened request's own action: the rejection / hold
 * conversation, the CS contact flag, notes, SCR payload. `suppressCsEmailForMps` keeps the
 * server's per-request hold email off the siblings, so a family hold raises a single CS task.
 * Non-MPS requests get an empty list, so callers can spread this unconditionally.
 */
export const mpsFamilyDecisionActions = (
  medRequest: MedRequest,
  decision: { action: 'reject' | 'hold'; reason: string }
): MedRequestMutationFnProps[] => {
  if (!isMpsMedRequest(medRequest)) return []

  return pendingMpsFamilyRows(medRequest).map(row => ({ id: row.id, ...decision, suppressCsEmailForMps: true }))
}

/**
 * Fires the updates one at a time — the family's requests all mutate the shared subscription
 * order, so parallel updates could race on it — and stops at the first failure so the rows not
 * yet actioned stay pending for a retry. Failures surface via the mutation's own onError toast,
 * which is why the chain itself swallows them; the returned counts let the caller decide
 * whether to navigate away or keep the page for a retry.
 */
export const runMedRequestActionChain = async (
  send: (variables: MedRequestMutationFnProps) => Promise<unknown>,
  actions: MedRequestMutationFnProps[]
): Promise<{ ok: boolean; completed: number; total: number }> => {
  let completed = 0
  for (const variables of actions) {
    try {
      await send(variables)
      completed++
    } catch {
      break
    }
  }
  return { ok: completed === actions.length, completed, total: actions.length }
}
